import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';
import * as api from './api';
import type {
  AnswerJobInputBody,
  CreateJobBody,
  PostJobMessageBody,
  ResumeJobBody,
  UpdateJobBody,
} from './schemas';

function useActiveTenantId(): string {
  const tenantId = useAuthStore((s) => s.tenantUser?.tenantId);
  if (!tenantId) throw new Error('useActiveTenantId called without an active tenant');
  return tenantId;
}

export function useJobsList() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['jobs', tenantId] as const,
    queryFn: () => api.listJobs(tenantId),
    refetchInterval: 5000,
  });
}

// ─── Jobs page (Yours + Suggested) ─────────────────────────────────────
export function useJobsPage() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['jobs-page', tenantId] as const,
    queryFn: () => api.getJobsPage(tenantId),
  });
}

export function useFavoriteTemplate() {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, favorite }: { templateId: string; favorite: boolean }) =>
      favorite
        ? api.favoriteTemplate(tenantId, templateId)
        : api.unfavoriteTemplate(tenantId, templateId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs-page', tenantId] }),
  });
}

export function useDismissSuggestedTemplate() {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => api.dismissSuggestedTemplate(tenantId, templateId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs-page', tenantId] }),
  });
}

export function useUndismissAllSuggestions() {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.undismissAllSuggestions(tenantId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs-page', tenantId] }),
  });
}

export function useHideTemplateFromJobs() {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => api.hideTemplateFromJobs(tenantId, templateId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs-page', tenantId] }),
  });
}

export function useJob(id: string) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['job', tenantId, id] as const,
    queryFn: () => api.getJob(tenantId, id),
    // Poll every 3s while the Job is in-flight (running OR awaiting_input
    // for a paused-Aria question). Terminal states stop polling.
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === 'running' || s === 'awaiting_input' ? 3000 : false;
    },
  });
}

export function useJobRecommendations(templateId: string | null) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['job-recommendations', tenantId, templateId ?? '__none'] as const,
    queryFn: () => api.getJobRecommendations(tenantId, templateId!),
    enabled: Boolean(templateId),
  });
}

export function useCreateJob() {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateJobBody) => api.createJob(tenantId, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['jobs', tenantId] });
    },
  });
}

export function useUpdateJob(id: string) {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateJobBody) => api.updateJob(tenantId, id, body),
    onSuccess: async (item) => {
      qc.setQueryData(['job', tenantId, id], item);
      await qc.invalidateQueries({ queryKey: ['jobs', tenantId] });
    },
  });
}

export function useCancelJob(id: string) {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.cancelJob(tenantId, id),
    onSuccess: async (item) => {
      qc.setQueryData(['job', tenantId, id], item);
      await qc.invalidateQueries({ queryKey: ['jobs', tenantId] });
    },
  });
}

export function useDeleteJob(id: string) {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteJob(tenantId, id),
    onSuccess: async () => {
      qc.removeQueries({ queryKey: ['job', tenantId, id] });
      await qc.invalidateQueries({ queryKey: ['jobs', tenantId] });
    },
  });
}

// ─── Job Detail lifecycle hooks ────────────────────────────────────────

export function usePauseJob(id: string) {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.pauseJob(tenantId, id),
    onSuccess: (item) => qc.setQueryData(['job', tenantId, id], item),
  });
}

export function useResumeJob(id: string) {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ResumeJobBody = {}) => api.resumeJob(tenantId, id, body),
    onSuccess: (item) => qc.setQueryData(['job', tenantId, id], item),
  });
}

export function usePostJobMessage(id: string) {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PostJobMessageBody) => api.postJobMessage(tenantId, id, body),
    onSuccess: (item) => qc.setQueryData(['job', tenantId, id], item),
  });
}

export function useAnswerJobInput(id: string) {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AnswerJobInputBody) => api.answerJobInput(tenantId, id, body),
    onSuccess: (item) => qc.setQueryData(['job', tenantId, id], item),
  });
}

export function useAcknowledgeJob(id: string) {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.acknowledgeJob(tenantId, id),
    onSuccess: async (item) => {
      qc.setQueryData(['job', tenantId, id], item);
      // Invalidate dashboard + activity queries so the blocked count and
      // Needs Attention list refresh immediately.
      await qc.invalidateQueries({ queryKey: ['jobs', tenantId] });
      await qc.invalidateQueries({ queryKey: ['activity', tenantId] });
      await qc.invalidateQueries({ queryKey: ['dashboard', tenantId] });
    },
  });
}

export function useSetJobLastSeen(id: string) {
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: (turnNumber: number) => api.setJobLastSeen(tenantId, id, turnNumber),
  });
}

export function useCreateAdHocJob() {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<CreateJobBody, 'job_template_id' | 'inputs' | 'staff_profile_id'>) =>
      api.createJob(tenantId, { ...body, inputs: {} }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['jobs', tenantId] });
    },
  });
}

export function useSaveJobAsTemplate(jobId: string) {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string }) => api.saveJobAsTemplate(tenantId, jobId, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['jobs-page', tenantId] });
    },
  });
}
